import { cn } from "@/lib/utils";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { motion } from "framer-motion";
import { X } from "lucide-react";
import { type ReactNode, useLayoutEffect, useRef, useState } from "react";
import { usePortalContainer } from "../../../shared/portal-container-context";
import optionOther from "../assets/questionnaire/option-other.svg?inline";
import optionPurchaseOrders from "../assets/questionnaire/option-purchase-orders.svg?inline";
import optionSupplierBills from "../assets/questionnaire/option-supplier-bills.svg?inline";
import optionUnpaidInvoices from "../assets/questionnaire/option-unpaid-invoices.svg?inline";
import sectionCardCash from "../assets/section-card-cash.svg?inline";
import sectionCardFund from "../assets/section-card-fund.svg?inline";
import sectionCardPay from "../assets/section-card-pay.svg?inline";
import { INTERACTIVE_FOCUS } from "../lib/chrome";
import { LANDING_CONTAINER_CLASS } from "../lib/layout";
import { EASE_CALM } from "../lib/motion";
import { ChatAvatar } from "../sections/two/chat/chat-avatar";
import { LandingBubble } from "../shared/messaging/landing-bubble";
import {
	landingBubblePaddingClass,
	landingBubbleShellClass,
	landingBubbleTextClass,
} from "../shared/messaging/landing-bubble-styles";
import { LandingMessage, LandingMessageContent } from "../shared/messaging/landing-message";
import {
	ICON_GLYPH_CLASS,
	ICON_TEXT_BOTTOM_CLASS,
	ICON_TEXT_TILE_CLASS,
	ICON_TEXT_TILE_SIZE_CLASS,
	ICON_TEXT_TOP_CLASS,
	QUESTION_LABEL_TYPE_CLASS,
} from "./choice-card-layout";
import { QuestionnaireComplete } from "./questionnaire-complete";
import { useQuestionnaireFlow } from "./questionnaire-context";
import {
	QUESTIONNAIRE_OPTIONS_CLUSTER_CLASS,
	QUESTIONNAIRE_TRANSCRIPT_LIST_CLASS,
	QUESTIONNAIRE_TRANSCRIPT_OPTIONS_CLASS,
	QUESTIONNAIRE_TRANSCRIPT_QUESTION_CLASS,
	QUESTIONNAIRE_TRANSCRIPT_ROW_BASE_CLASS,
	QUESTIONNAIRE_TRANSCRIPT_ROW_GAP_CLASS,
	questionnaireOptionsGridClass,
} from "./questionnaire-option-layout";
import "./questionnaire-options.css";
import {
	EQuestionnaireEvent,
	EStepSelection,
	JOURNEY_STEP_ID,
	type TQuestionnaireOptionIcon,
	type TTranscriptAgentEntry,
	type TTranscriptOptionsEntry,
	buildTranscript,
	groupTranscriptTurns,
	isJourneyPathId,
} from "./questionnaire-flow";
import { preventQuestionnairePointerScroll } from "./questionnaire-scroll";
import { StepEnter } from "./step-enter";

const OPTION_ICONS: Record<TQuestionnaireOptionIcon, string> = {
	purchaseOrders: optionPurchaseOrders,
	unpaidInvoices: optionUnpaidInvoices,
	supplierBills: optionSupplierBills,
	other: optionOther,
	fund: sectionCardFund,
	payments: sectionCardPay,
	cashFlow: sectionCardCash,
};

function AgentTurn({
	entry,
	intro,
	animate,
}: {
	entry: TTranscriptAgentEntry;
	intro?: string;
	animate: boolean;
}) {
	return (
		<LandingMessage align="start" className="max-w-[min(792px,100%)] gap-9 max-md:max-w-[85%] max-md:gap-3">
			<StepEnter enabled={animate} delayMs={0} durationMs={900} className="shrink-0 self-start">
				<ChatAvatar messageRole="assistant" fit="full" className="mt-1.5 h-[50px] w-[52px] max-md:size-[44px]" />
			</StepEnter>
			<LandingMessageContent className="min-w-0 gap-5">
				{intro ? (
					<StepEnter enabled={animate} delayMs={200} durationMs={960}>
						<LandingBubble
							className="max-w-full"
							contentClassName={cn(
								landingBubbleShellClass("received", "agent"),
								landingBubblePaddingClass("received", "agent", "px-[25px] py-[18px]"),
								landingBubbleTextClass("received", "agent"),
							)}
						>
							<p className="m-0">{intro}</p>
						</LandingBubble>
					</StepEnter>
				) : null}
				<StepEnter enabled={animate} delayMs={420} durationMs={1000}>
					<LandingBubble
						className="max-w-full"
						contentClassName={cn(
							landingBubbleShellClass("received", "agent"),
							landingBubblePaddingClass("received", "agent", "px-[25px] py-5"),
							landingBubbleTextClass("received", "agent"),
						)}
					>
						<p className="m-0">{entry.question}</p>
						{entry.hint ? (
							<p className="m-0 mt-1.5 text-[13px] font-normal leading-[1.3] tracking-[-0.03em] text-teal-500">
								{entry.hint}
							</p>
						) : null}
					</LandingBubble>
				</StepEnter>
			</LandingMessageContent>
		</LandingMessage>
	);
}

function QuestionnaireOptionGrid({ entry }: { entry: TTranscriptOptionsEntry }) {
	const { isAdvancing, settlingSelection, dispatch, advanceToNextStep, selectSingleOption, changePath } =
		useQuestionnaireFlow();
	const isMulti = entry.selection === EStepSelection.MULTI;
	const optionCount = entry.options.length;
	const hasOptionIcons = entry.options.some((option) => option.icon);
	const settlingThisStep = settlingSelection?.stepIndex === entry.stepIndex;

	return (
		<>
			<div
				className={questionnaireOptionsGridClass(optionCount)}
				role={isMulti ? "group" : "radiogroup"}
				data-questionnaire-options=""
				data-questionnaire-step={entry.stepId}
				aria-label={entry.question}
			>
				{entry.options.map((option, index) => {
					const selected = isMulti
						? entry.selectedIds.includes(option.id)
						: settlingThisStep
							? settlingSelection?.optionId === option.id
							: entry.selectedIds.includes(option.id);
					const iconSrc = option.icon ? OPTION_ICONS[option.icon] : null;
					return (
						<StepEnter
							key={option.id}
							enabled={entry.isCurrent}
							delayMs={720 + index * 160}
							durationMs={840}
							className={cn("h-full w-full", hasOptionIcons && ICON_TEXT_TILE_CLASS)}
						>
							<button
								type="button"
								className={cn(
									"group/option h-full w-full bg-white text-center leading-[1.3] tracking-[-0.03em] text-teal-500 shadow-[0_10px_60px_color-mix(in_srgb,var(--color-black)_2%,transparent)] [transition:background-color_400ms_cubic-bezier(0.4,0,0.2,1),color_400ms_cubic-bezier(0.4,0,0.2,1)]",
									"hover:bg-teal-500 hover:text-white",
									INTERACTIVE_FOCUS,
									hasOptionIcons
										? cn(ICON_TEXT_TILE_CLASS, ICON_TEXT_TILE_SIZE_CLASS)
										: cn(
												"flex h-full items-center justify-center px-5 py-4 text-center max-md:px-3 max-md:py-3",
												QUESTION_LABEL_TYPE_CLASS,
											),
									{
										"bg-teal-500 text-white": selected,
										"max-lg:hover:bg-white max-lg:hover:text-teal-500": !selected,
										"max-lg:active:bg-teal-500 max-lg:active:text-white": !selected,
									},
								)}
								aria-pressed={isMulti ? selected : undefined}
								aria-checked={!isMulti ? selected : undefined}
								role={isMulti ? "checkbox" : "radio"}
								data-questionnaire-option=""
								data-has-icon={hasOptionIcons ? "" : undefined}
								data-selected={selected ? "" : undefined}
								onPointerDown={preventQuestionnairePointerScroll}
								onClick={() => {
									if (isMulti) {
										if (!entry.isCurrent) return;
										dispatch({ type: EQuestionnaireEvent.TOGGLE_OPTION, optionId: option.id });
										return;
									}
									if (entry.stepId === JOURNEY_STEP_ID && isJourneyPathId(option.id)) {
										changePath(option.id);
										return;
									}
									selectSingleOption(option.id, entry.stepIndex);
								}}
							>
								{iconSrc ? (
									<span className={ICON_TEXT_TOP_CLASS}>
										<img
											className={cn(ICON_GLYPH_CLASS, {
												"brightness-0 invert": selected,
												"max-lg:group-hover/option:brightness-100 max-lg:group-hover/option:invert-0": !selected,
												"max-lg:group-active/option:brightness-0 max-lg:group-active/option:invert": !selected,
											})}
											src={iconSrc}
											alt=""
											aria-hidden="true"
										/>
									</span>
								) : null}
								<span className={cn(hasOptionIcons ? ICON_TEXT_BOTTOM_CLASS : "max-w-[191px]")}>{option.label}</span>
							</button>
						</StepEnter>
					);
				})}
			</div>

			{isMulti && entry.isCurrent ? (
				<StepEnter enabled delayMs={960} durationMs={800}>
					<button
						type="button"
						className={cn(
							"inline-flex h-[46px] items-center justify-center bg-green-500 px-8 text-[14px] font-normal tracking-[-0.02em] text-teal-600 disabled:opacity-40",
							INTERACTIVE_FOCUS,
						)}
						disabled={entry.selectedIds.length === 0 || isAdvancing || settlingSelection !== null}
						onPointerDown={preventQuestionnairePointerScroll}
						onClick={() => {
							if (isAdvancing) return;
							advanceToNextStep({ type: EQuestionnaireEvent.CONTINUE });
						}}
					>
						Continue
					</button>
				</StepEnter>
			) : null}
		</>
	);
}

export function TranscriptRow({
	stepId,
	current,
	question,
	options,
}: {
	stepId: string;
	current: boolean;
	question: ReactNode;
	options?: ReactNode;
}) {
	return (
		<div
			className={cn(QUESTIONNAIRE_TRANSCRIPT_ROW_BASE_CLASS, QUESTIONNAIRE_TRANSCRIPT_ROW_GAP_CLASS)}
			data-questionnaire-turn={stepId}
			data-questionnaire-turn-current={current ? "" : undefined}
		>
			<div className={QUESTIONNAIRE_TRANSCRIPT_QUESTION_CLASS}>{question}</div>
			{options ? (
				<div className={QUESTIONNAIRE_TRANSCRIPT_OPTIONS_CLASS}>
					<div className={QUESTIONNAIRE_OPTIONS_CLUSTER_CLASS}>{options}</div>
				</div>
			) : null}
		</div>
	);
}

const QUESTIONNAIRE_DIALOG_DESCRIPTION =
	"Answer a few questions so we can point you to the right next step. Press Escape to close.";

type TQuestionnaireModalFrameProps = {
	title: string;
	onClose: () => void;
	onOpenAutoFocus?: (event: Event) => void;
	children: ReactNode;
};

/** Shared overlay + glass panel. Used by picker and path steps. Square edges. */
export function QuestionnaireModalFrame({ title, onClose, onOpenAutoFocus, children }: TQuestionnaireModalFrameProps) {
	const portalContainer = usePortalContainer();

	return (
		<DialogPrimitive.Portal container={portalContainer ?? undefined}>
			<DialogPrimitive.Overlay asChild>
				<motion.div
					className="fixed inset-0 z-[100] bg-teal-600/50"
					initial={{ opacity: 0 }}
					animate={{ opacity: 1 }}
					transition={{ duration: 0.36, ease: EASE_CALM }}
				/>
			</DialogPrimitive.Overlay>
			<DialogPrimitive.Content asChild onOpenAutoFocus={onOpenAutoFocus} onEscapeKeyDown={() => onClose()}>
				<motion.div
					data-questionnaire-dialog=""
					className={cn(
						"fixed z-[101] inset-8 flex flex-col overflow-hidden bg-white/88 text-teal-500 shadow-[0_18px_200px_color-mix(in_srgb,var(--color-teal-500)_7%,transparent)] outline-none backdrop-blur-[8px] backdrop-saturate-125",
						"max-md:inset-3",
						"font-sans",
					)}
					initial={{ opacity: 0, y: 10, scale: 0.994 }}
					animate={{ opacity: 1, y: 0, scale: 1 }}
					transition={{ duration: 0.42, ease: EASE_CALM }}
				>
					<DialogPrimitive.Title className="sr-only">{title}</DialogPrimitive.Title>
					<DialogPrimitive.Description className="sr-only">
						{QUESTIONNAIRE_DIALOG_DESCRIPTION}
					</DialogPrimitive.Description>

					<DialogPrimitive.Close
						className={cn(
							"absolute top-0 right-0 z-[2] flex size-10 items-center justify-center border border-grey-700 border-t-0 border-r-0 bg-white text-teal-500 hover:bg-greige-500",
							INTERACTIVE_FOCUS,
						)}
						aria-label="Close questionnaire"
					>
						<X aria-hidden="true" className="size-5" />
					</DialogPrimitive.Close>

					{children}
				</motion.div>
			</DialogPrimitive.Content>
		</DialogPrimitive.Portal>
	);
}

export function QuestionnaireDialogShell({ children }: { children: ReactNode }) {
	const { step, stepPanelRef, handleOpenChange } = useQuestionnaireFlow();

	return (
		<QuestionnaireModalFrame
			title={step?.question ?? "Triffin questionnaire"}
			onClose={() => handleOpenChange(false)}
			onOpenAutoFocus={(event) => {
				event.preventDefault();
				stepPanelRef.current?.focus();
			}}
		>
			{children}
		</QuestionnaireModalFrame>
	);
}

export function QuestionnaireStep({ children }: { children: ReactNode }) {
	const { stepPanelRef } = useQuestionnaireFlow();
	const columnRef = useRef<HTMLDivElement>(null);
	const centredMarginRef = useRef<number | null>(null);
	const [pinnedMarginTop, setPinnedMarginTop] = useState<number | null>(null);

	/* Live auto margins re-centre the transcript on every height change. The first time the
	 * centred margin moves, pin it at its previous value so the transcript keeps a fixed
	 * anchor and new turns fade in below. Desktop margins are always 0, so the pin never
	 * engages there. */
	useLayoutEffect(() => {
		const column = columnRef.current;
		if (!column) return;
		const marginTop = Number.parseFloat(getComputedStyle(column).marginTop) || 0;
		const centred = centredMarginRef.current;
		centredMarginRef.current = marginTop;
		if (pinnedMarginTop === null && centred !== null && centred !== marginTop) {
			setPinnedMarginTop(centred);
		}
	});

	return (
		<div
			ref={stepPanelRef}
			tabIndex={-1}
			className="relative flex min-h-0 flex-1 flex-col overflow-y-auto px-6 pb-10 pt-[var(--landing-nav-height,72px)] scroll-pt-[var(--landing-nav-height,72px)] outline-none max-md:pt-[var(--landing-nav-height,3.5rem)] max-md:scroll-pt-[var(--landing-nav-height,3.5rem)] max-md:pb-8 md:px-10"
		>
			{/* max-md:my-auto centres short steps vertically on mobile; auto margins collapse to 0
			 * once the transcript overflows, so long steps still scroll from the top. */}
			<div
				ref={columnRef}
				className={cn(LANDING_CONTAINER_CLASS, QUESTIONNAIRE_TRANSCRIPT_LIST_CLASS, "max-md:my-auto")}
				style={pinnedMarginTop !== null ? { marginTop: pinnedMarginTop } : undefined}
			>
				{children}
			</div>
		</div>
	);
}

/**
 * Full transcript: journey turn first, then every answered step and the live
 * step keep their question bubble and choice grid. Complete is the last turn.
 */
export function QuestionnaireChat() {
	const { state } = useQuestionnaireFlow();
	const turns = groupTranscriptTurns(buildTranscript(state).filter((entry) => entry.stepId !== JOURNEY_STEP_ID));

	return (
		<>
			{turns.map((turn) => {
				if (turn.kind === "complete") {
					return <QuestionnaireComplete key={`complete-${turn.stepId}`} />;
				}

				return (
					<TranscriptRow
						key={`turn-${turn.stepId}`}
						stepId={turn.stepId}
						current={turn.agent.isCurrent}
						question={<AgentTurn entry={turn.agent} intro={turn.agent.intro} animate={turn.agent.isCurrent} />}
						options={<QuestionnaireOptionGrid entry={turn.options} />}
					/>
				);
			})}
		</>
	);
}

export function QuestionnaireOptions() {
	const { state, steps } = useQuestionnaireFlow();

	return (
		<p className="sr-only">
			Step {Math.min(state.stepIndex + 1, steps.length)} of {steps.length || 1}
		</p>
	);
}

export function QuestionnaireProgress() {
	const { progress } = useQuestionnaireFlow();
	return (
		<div
			className="relative h-[5px] w-full shrink-0 bg-white"
			role="progressbar"
			tabIndex={0}
			aria-valuemin={0}
			aria-valuemax={100}
			aria-valuenow={Math.round(progress * 100)}
			aria-label="Questionnaire progress"
		>
			<div
				className="h-full bg-purple-500 transition-[width] duration-700 ease-[cubic-bezier(0.4,0,0.2,1)] motion-reduce:transition-none"
				style={{ width: `${progress * 100}%` }}
			/>
		</div>
	);
}
